import * as THREE from 'three'
import { Gradient } from "whatamesh";

// Gradient
const gradient = new Gradient();
gradient.initGradient("#gradient-canvas");

/**
 * Scene
 */
const scene = new THREE.Scene()
scene.background = null

/**
 * Camera
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}
const camera = new THREE.PerspectiveCamera(15, sizes.width / sizes.height, 0.1, 1000)
camera.position.z = 10
camera.lookAt(0, 0, 0)
scene.add(camera)


/**
 * Canvas & Renderer
 */
const canvas = document.querySelector('canvas.webgl')
const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true,
    stencil: true,
    alpha: true
})
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
renderer.setSize(sizes.width, sizes.height)
document.body.appendChild(renderer.domElement)


/**
 * Textures
 */
const textureLoader = new THREE.TextureLoader()

// Kleine Hilfsfunktion, um Texturen zu laden & sRGBEncoding zu setzen
function loadAndConfigureTexture(path, repeat = false) {
    const tex = textureLoader.load(path, (texture) => {
        texture.encoding = THREE.RGBEncoding
    })
    tex.needsUpdate = true
    tex.anisotropy = 8
    if (repeat) {
        tex.wrapS = THREE.RepeatWrapping
        tex.wrapT = THREE.RepeatWrapping
    }
    return tex
}

// Texturen laden
const frameTexture    = loadAndConfigureTexture('./textures/frame.png')
const maskTexture     = loadAndConfigureTexture('./textures/mask2.png')
const planeTexture1   = loadAndConfigureTexture('./textures/layer1-02.png')
const planeTexture2   = loadAndConfigureTexture('./textures/layer2.png')
const planeTexture3   = loadAndConfigureTexture('./textures/layer3.png')
const planeTexture4   = loadAndConfigureTexture('./textures/layer4.png', true)
const planeTexture5   = loadAndConfigureTexture('./textures/layer5.png')
const stencilTexture  = loadAndConfigureTexture('./textures/stencil.png')

/**
 * Sepia Umwandlung
 */

const sepiaFunction = `
    vec3 toSepia(vec3 color) {
        float gray = dot(color, vec3(0.299, 0.587, 0.114));
        float brightness = 0.12;
        float contrast = 0.83;
        gray = (gray - 0.5) * contrast + 0.5 + brightness;
        return vec3(gray * 1.04, gray * 0.95, gray * 0.82);
    }
`;

/**
 * Group & Geometry
 */
const group = new THREE.Group()
scene.add(group)

// Plane-Größe
const planeAspectRatio = 5 / 6
const planeWidth = 2
const planeHeight = planeWidth / planeAspectRatio
const planeGeometry = new THREE.PlaneGeometry(planeWidth, planeHeight)

/**
 * Stencil Mask
 */
const stencilMaskGeometry = new THREE.PlaneGeometry(planeWidth, planeHeight)
const stencilMaskMaterial = new THREE.MeshBasicMaterial({
    map: stencilTexture,
    stencilWrite: true,
    stencilRef: 1,
    stencilFunc: THREE.AlwaysStencilFunc,
    stencilZPass: THREE.ReplaceStencilOp,
    stencilZFail: THREE.KeepStencilOp,
    stencilFail: THREE.KeepStencilOp,
    colorWrite: false,
    depthWrite: false,
    depthTest: false,
    transparent: true,
    alphaTest: 0.5
})
const stencilMask = new THREE.Mesh(stencilMaskGeometry, stencilMaskMaterial)
stencilMask.renderOrder = 0
stencilMask.scale.set(1.0, 1.0, 1.0)
group.add(stencilMask)

/**
 * Layer-Materialien
 */

// Wellen-Animation (Layer 1)
const createAnimatedLayerMaterial = () =>
    new THREE.ShaderMaterial({
        uniforms: {
            map: { value: planeTexture1 },
            time: { value: 0 },
            isActive: { value: 1.0 }
        },
        vertexShader: `
            varying vec2 vUv;
            void main() {
                vUv = uv;
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
        `,
        fragmentShader: `
            ${sepiaFunction}

            uniform sampler2D map;
            uniform float time;
            uniform float isActive;
            varying vec2 vUv;

            void main() {
                vec2 animatedUv = vUv;
                animatedUv.y += sin(time * 1.2 + vUv.x * 10.0) * 0.015;
                
                vec4 texColor = texture2D(map, animatedUv);
                vec3 finalColor = mix(toSepia(texColor.rgb), texColor.rgb, isActive);
                gl_FragColor = vec4(finalColor, texColor.a);
            }
        `,
        transparent: true,
        side: THREE.DoubleSide,
        stencilWrite: true,
        stencilRef: 1,
        stencilFunc: THREE.EqualStencilFunc,
        stencilZPass: THREE.KeepStencilOp,
        stencilZFail: THREE.KeepStencilOp,
        stencilFail: THREE.KeepStencilOp,
        depthTest: true,
        depthWrite: true
    });

// Schiff (Layer 2)
const createAnimatedLayer2Material = () =>
    new THREE.ShaderMaterial({
        uniforms: {
            map: { value: planeTexture2 },
            time: { value: 0 },
            isActive: { value: 1.0 }
        },
        vertexShader: `
            varying vec2 vUv;
            void main() {
                vUv = uv;
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
        `,
        fragmentShader: `
            ${sepiaFunction}

            uniform sampler2D map;
            uniform float time;
            uniform float isActive;
            varying vec2 vUv;
            
            void main() {
                vec2 center = vec2(0.5, 0.5);
                float radius = 0.01;
                
                vec2 animatedUv = vUv;
                animatedUv.x -= cos(time * 0.75) * (radius * 0.40);
                animatedUv.y -= sin(time * 0.75) * radius;
                
                // Rotation
                float maxAngle = radians(1.0);
                float angle = maxAngle * sin(time * 0.6);
                
                vec2 rotatedUv = animatedUv - center;
                float s = sin(angle);
                float c = cos(angle);
                rotatedUv = vec2(
                    rotatedUv.x * c - rotatedUv.y * s,
                    rotatedUv.x * s + rotatedUv.y * c
                );
                rotatedUv += center;
                
                vec4 texColor = texture2D(map, rotatedUv);
                vec3 finalColor = mix(toSepia(texColor.rgb), texColor.rgb, isActive);
                gl_FragColor = vec4(finalColor, texColor.a);
            }
        `,
        transparent: true,
        side: THREE.DoubleSide,
        stencilWrite: true,
        stencilRef: 1,
        stencilFunc: THREE.EqualStencilFunc,
        stencilZPass: THREE.KeepStencilOp,
        stencilZFail: THREE.KeepStencilOp,
        stencilFail: THREE.KeepStencilOp,
        depthTest: true,
        depthWrite: true
    });
    
// Wolken (Layer 4)
const createAnimatedLayer4Material = () =>
    new THREE.ShaderMaterial({
        uniforms: {
            map: { value: planeTexture4 },
            time: { value: 0 },
            isActive: { value: 1.0 }
        },
        vertexShader: `
            varying vec2 vUv;
            void main() {
                vUv = uv;
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
        `,
        fragmentShader: `
            ${sepiaFunction}

            uniform sampler2D map;
            uniform float time;
            uniform float isActive;
            varying vec2 vUv;

            void main() {
                vec2 animatedUv = vUv;
                animatedUv.x += time * 0.015;
                
                vec4 texColor = texture2D(map, animatedUv);
                vec3 finalColor = mix(toSepia(texColor.rgb), texColor.rgb, isActive);
                gl_FragColor = vec4(finalColor, texColor.a);
            }
        `,
        transparent: true,
        side: THREE.DoubleSide,
        stencilWrite: true,
        stencilRef: 1,
        stencilFunc: THREE.EqualStencilFunc,
        stencilZPass: THREE.KeepStencilOp,
        stencilZFail: THREE.KeepStencilOp,
        stencilFail: THREE.KeepStencilOp,
        depthTest: true,
        depthWrite: true
    });

// Statische Layer (3 & 5)
const createLayerMaterial = (layerTexture) =>
    new THREE.ShaderMaterial({
        uniforms: {
            map: { value: layerTexture },
            mask: { value: maskTexture },
            isActive: { value: 1.0 }
        },
        vertexShader: `
            varying vec2 vUv;
            void main() {
                vUv = uv;
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
        `,
        fragmentShader: `
            ${sepiaFunction}

            uniform sampler2D map;
            uniform sampler2D mask;
            uniform float isActive;
            varying vec2 vUv;

            void main() {
                vec4 texColor = texture2D(map, vUv);
                vec3 finalColor = mix(toSepia(texColor.rgb), texColor.rgb, isActive);
                gl_FragColor = vec4(finalColor, texColor.a);
            }
        `,
        transparent: true,
        side: THREE.DoubleSide,
        stencilWrite: true,
        stencilRef: 1,
        stencilFunc: THREE.EqualStencilFunc,
        stencilZPass: THREE.KeepStencilOp,
        stencilZFail: THREE.KeepStencilOp,
        stencilFail: THREE.KeepStencilOp,
        depthTest: true,
        depthWrite: true
    });

// Front Frame
const createFrameMaterial = () =>
    new THREE.ShaderMaterial({
        uniforms: {
            map: { value: frameTexture },
            mask: { value: maskTexture }
        },
        vertexShader: `
            varying vec2 vUv;
            void main() {
                vUv = uv;
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
        `,
        fragmentShader: `
            uniform sampler2D map;
            uniform sampler2D mask;
            varying vec2 vUv;

            void main() {
                vec4 frameColor = texture2D(map, vUv);
                vec4 maskColor = texture2D(mask, vUv);
                float alpha = maskColor.g * maskColor.r;
                gl_FragColor = vec4(frameColor.rgb, alpha);
            }
        `,
        transparent: true,
        side: THREE.DoubleSide
    })

/**
 * Meshes
 */
const plane1 = new THREE.Mesh(planeGeometry, createAnimatedLayerMaterial())
plane1.renderOrder = 1
plane1.material.uniforms.isActive.value = 0.0;
group.add(plane1)

const plane2 = new THREE.Mesh(planeGeometry, createAnimatedLayer2Material())
plane2.position.z = -0.1
plane2.renderOrder = 1
plane2.material.uniforms.isActive.value = 0.0;
group.add(plane2)

const plane4 = new THREE.Mesh(planeGeometry, createAnimatedLayer4Material())
plane4.position.z = -0.2
plane4.renderOrder = 1
plane4.material.uniforms.isActive.value = 0.0;
group.add(plane4)

// Insel (3) & Himmel (5)
const layerTextures = [planeTexture3, planeTexture5]
layerTextures.forEach((texture, i) => {
    const plane = new THREE.Mesh(planeGeometry, createLayerMaterial(texture))
    plane.position.z = -0.1 * (i + 2)
    plane.renderOrder = 1
    plane.material.uniforms.isActive.value = 0.0;
    group.add(plane)
})

// Frame
const frame = new THREE.Mesh(planeGeometry, createFrameMaterial())
frame.scale.set(0.99, 0.99, 0.99)
frame.position.z = 0.01
frame.renderOrder = 2
group.add(frame)

// Fügen Sie diese Variable am Anfang Ihres Codes hinzu
let lastMouseMoveTime = 0;
let isActive = true;
let sepiaTransitionFactor = 0.0;

/**
 * Events: Resize & Mausbewegung
 */
window.addEventListener('resize', () => {
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    renderer.setSize(sizes.width, sizes.height)
})

const mouse = new THREE.Vector2()
let lastMousePosition = new THREE.Vector2()
const movementThreshold = 0.01; // Schwellenwert für die Bewegung

setTimeout(() => {
    document.addEventListener('mousemove', (event) => {
        // Aktuelle Mausposition aktualisieren (für die Rotation)
        mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
        mouse.y = -((event.clientY / window.innerHeight) * 2 - 1);
        
        // Bewegungsdistanz berechnen
        const movement = new THREE.Vector2(
            mouse.x - lastMousePosition.x,
            mouse.y - lastMousePosition.y
        );
        const movementDistance = movement.length();
        
        // Wenn die Bewegung groß genug ist, aktiviere die Animation
        if (movementDistance > movementThreshold) {
            lastMouseMoveTime = Date.now();
            isActive = false;
        }
        
        // Aktuelle Position für nächste Berechnung speichern
        lastMousePosition.copy(mouse);
    });
}, 800);

/**
 * Gyro & Mobile
 */
const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
const gyro = new THREE.Vector2()
const targetRotation = new THREE.Vector2()
const lerpFactor = 0.1

const maxRotationDesktop = THREE.MathUtils.degToRad(25)
const maxRotationMobile = THREE.MathUtils.degToRad(25)

let isUsingGyro = false

function handleGyro(event) {
    if (event.beta === null || event.gamma === null) return
    gyro.y = THREE.MathUtils.clamp(event.beta / 45, -1, 1)
    gyro.x = THREE.MathUtils.clamp(event.gamma / 45, -1, 1)
}

function checkGyroAvailability() {
    return new Promise((resolve) => {
        if (window.DeviceOrientationEvent) {
            if (typeof DeviceOrientationEvent.requestPermission === 'function') {
                DeviceOrientationEvent.requestPermission()
                    .then((permissionState) => {
                        if (permissionState === 'granted') {
                            window.addEventListener('deviceorientation', handleGyro)
                            isUsingGyro = true
                        }
                        resolve(permissionState === 'granted')
                    })
                    .catch(console.error)
            } else {
                window.addEventListener('deviceorientation', handleGyro)
                isUsingGyro = true
                resolve(true)
            }
        } else {
            resolve(false)
        }
    })
}

if (isMobile) {
    checkGyroAvailability().then((hasGyro) => {
        console.log('Gyro available:', hasGyro)
    })
}

/**
 * Animation
 */
const clock = new THREE.Clock()
let previousTime = 0
const sepiaTransitionDuration = 1.0;
const animationTransitionDuration = 2.0;
const transitionTimeout = 500;
let totalPausedTime = 0;
let animationTransitionProgress = 0.0;


function tick() {
    const elapsedTime = clock.getElapsedTime()
    const deltaTime = elapsedTime - previousTime
    previousTime = elapsedTime

    // Steuerung mit Gyro oder Maus
    if (isMobile && isUsingGyro) {
        targetRotation.x = THREE.MathUtils.lerp(
            targetRotation.x,
            gyro.y * maxRotationMobile,
            lerpFactor
        )
        targetRotation.y = THREE.MathUtils.lerp(
            targetRotation.y,
            -gyro.x * maxRotationMobile,
            lerpFactor
        )
    } else {
        targetRotation.x = THREE.MathUtils.lerp(
            targetRotation.x,
            mouse.y * maxRotationDesktop,
            lerpFactor
        )
        targetRotation.y = THREE.MathUtils.lerp(
            targetRotation.y,
            mouse.x * maxRotationDesktop,
            lerpFactor
        )
    }

    group.rotation.x = targetRotation.x
    group.rotation.y = targetRotation.y

    // Überprüfe die Mausaktivität für Sepia und Animation
    const currentTime = Date.now();
    if (currentTime - lastMouseMoveTime > transitionTimeout) {
        isActive = true;  // Keine Mausbewegung -> Sepia + Animation pausiert
    }

    // Sanfte Animation des Sepia-Effekts und der Animations-Geschwindigkeit
    if (isActive) { // Sepia & Pause
        sepiaTransitionFactor -= deltaTime / sepiaTransitionDuration;
        if (sepiaTransitionFactor < 0.0) sepiaTransitionFactor = 0.0;
        
        animationTransitionProgress -= deltaTime / animationTransitionDuration;
        if (animationTransitionProgress < 0.0) animationTransitionProgress = 0.0;
    } else { // Farbe & Animation
        sepiaTransitionFactor += deltaTime / sepiaTransitionDuration;
        if (sepiaTransitionFactor > 1.0) sepiaTransitionFactor = 1.0;
        
        animationTransitionProgress += deltaTime / sepiaTransitionDuration;
        if (animationTransitionProgress > 1.0) animationTransitionProgress = 1.0;
    }
    // Bei animationTransitionProgress = 0 wird keine neue Zeit addiert
    totalPausedTime += deltaTime * (1.0 - animationTransitionProgress);
    const effectiveTime = elapsedTime - totalPausedTime;

    // Update alle Layer mit effectiveTime und Sepia
    const allPlanes = [plane1, plane2, plane4];
    allPlanes.forEach(plane => {
        plane.material.uniforms.time.value = effectiveTime;
        plane.material.uniforms.isActive.value = sepiaTransitionFactor;
    });

    // Update statische Layer - nur noch Sepia-Effekt
    group.children.forEach(child => {
        if (child.material && child.material.uniforms && child.material.uniforms.isActive) {
            child.material.uniforms.isActive.value = sepiaTransitionFactor;
        }
    });

    // Renderer-Stencil-Setup
    renderer.clearColor()
    renderer.clearStencil()
    renderer.clearDepth()

    // Stencil-Mask
    renderer.state.buffers.stencil.setTest(true)
    renderer.state.buffers.stencil.setMask(0xff)
    renderer.state.buffers.stencil.setClear(0)
    stencilMask.material.colorWrite = false
    stencilMask.material.depthWrite = false
    renderer.render(scene, camera)

    // Haupt-Scene
    renderer.state.buffers.stencil.setTest(true)
    renderer.render(scene, camera)

    requestAnimationFrame(tick)
}

tick()
